import React, { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Challenge, getChallengeLeaderboard, LeaderboardEntry } from "@/services/challengeService";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar, Trophy, Medal, Star } from "lucide-react";

interface ChallengeLeaderboardProps {
  challenge: Challenge;
}

export function ChallengeLeaderboard({ challenge }: ChallengeLeaderboardProps) {
  const queryClient = useQueryClient();
  
  const { data: entries = [], isLoading, refetch } = useQuery({
    queryKey: ['challengeLeaderboard', challenge.id],
    queryFn: () => getChallengeLeaderboard(challenge.id),
    staleTime: 0, // Always consider data stale to ensure freshness
    refetchOnWindowFocus: true, // Refresh when tab gets focus
  });

  // Refresh data when the challenge changes or component mounts
  useEffect(() => {
    console.log("Challenge changed or component mounted, refreshing leaderboard data for:", challenge.id);
    // Manually reset the cache to force a refresh
    queryClient.resetQueries({ queryKey: ['challengeLeaderboard', challenge.id] });
    // Fetch data for the current challenge
    refetch();
  }, [challenge.id, queryClient, refetch]);

  const isActive = new Date(challenge.startDate) <= new Date() && new Date(challenge.endDate) >= new Date();
  const hasEnded = new Date(challenge.endDate) < new Date();
  const notStartedYet = new Date(challenge.startDate) > new Date();
  
  const getMetricLabel = (type: string) => {
    switch (type) {
      case "leads_closed": return "Gewonnene Kunden";
      case "leads_contacted": return "Vereinbarte Termine";
      case "new_leads": return "Neue Leads";
      default: return "Punkte";
    }
  };

  // Top 3 winners
  const topThree = entries.slice(0, 3);
  // Rest of the leaderboard
  const restOfLeaderboard = entries.slice(3);

  return (
    <Card>
      <CardHeader className="border-b bg-muted/20">
        <div className="flex flex-col gap-1.5">
          <div className="flex justify-between items-start">
            <CardTitle>{challenge.title}</CardTitle>
            {isActive ? (
              <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">Aktiv</Badge>
            ) : hasEnded ? (
              <Badge variant="outline">Abgeschlossen</Badge>
            ) : (
              <Badge variant="outline">Startet bald</Badge>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{format(new Date(challenge.startDate), "dd.MM.yyyy", { locale: de })} - {format(new Date(challenge.endDate), "dd.MM.yyyy", { locale: de })}</span>
              </div>
              <div>
                <span className="font-medium">Metrik:</span> {getMetricLabel(challenge.metricType)}
              </div>
            </div>
          </div>
          
          <div className="mt-1">
            <p className="text-sm">{challenge.description}</p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        {isLoading ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-24 bg-muted/50 rounded"></div>
            <div className="h-64 bg-muted/30 rounded"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Display message if challenge hasn't started */}
            {notStartedYet && (
              <div className="bg-muted/30 p-4 rounded-md text-center">
                <p>Diese Challenge beginnt am {format(new Date(challenge.startDate), "dd.MM.yyyy", { locale: de })}.</p>
              </div>
            )}
            
            {/* Top 3 podium */}
            {!notStartedYet && topThree.length > 0 && (
              <div className="flex flex-col items-center">
                <h3 className="text-lg font-semibold mb-6">
                  <Trophy className="inline-block mr-2 text-amber-500" />
                  Bestenliste
                </h3>
                
                <div className="flex items-end justify-center w-full gap-3 md:gap-6 max-w-2xl mx-auto">
                  {/* Second place */}
                  {topThree[1] && (
                    <div className="flex-1 text-center order-1">
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center mx-auto mb-2 border-4 border-gray-100 shadow-md">
                        <span className="text-2xl font-bold">{topThree[1].userName.split(' ')[0]?.charAt(0) || ''}{topThree[1].userName.split(' ')[1]?.charAt(0) || ''}</span>
                      </div>
                      <div className="h-28 md:h-36 bg-gradient-to-b from-silver to-gray-100 rounded-t-lg flex flex-col items-center justify-center shadow-md">
                        <Medal className="h-6 w-6 text-gray-600 mb-1" />
                        <span className="font-semibold">{topThree[1].userName}</span>
                        <span className="text-lg font-bold">{topThree[1].score}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* First place */}
                  {topThree[0] && (
                    <div className="flex-1 text-center order-2">
                      <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 flex items-center justify-center mx-auto mb-2 border-4 border-yellow-100 shadow-lg">
                        <span className="text-3xl font-bold text-amber-900">{topThree[0].userName.split(' ')[0]?.charAt(0) || ''}{topThree[0].userName.split(' ')[1]?.charAt(0) || ''}</span>
                      </div>
                      <div className="h-36 md:h-48 bg-gradient-to-b from-amber-400 to-amber-100 rounded-t-lg flex flex-col items-center justify-center shadow-lg">
                        <Trophy className="h-8 w-8 text-amber-700 mb-1" />
                        <span className="font-semibold">{topThree[0].userName}</span>
                        <span className="text-xl font-bold">{topThree[0].score}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Third place */}
                  {topThree[2] && (
                    <div className="flex-1 text-center order-3">
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-amber-700 to-amber-600 flex items-center justify-center mx-auto mb-2 border-4 border-amber-100 shadow-md">
                        <span className="text-xl font-bold text-amber-100">{topThree[2].userName.split(' ')[0]?.charAt(0) || ''}{topThree[2].userName.split(' ')[1]?.charAt(0) || ''}</span>
                      </div>
                      <div className="h-24 md:h-28 bg-gradient-to-b from-amber-800 to-amber-200 rounded-t-lg flex flex-col items-center justify-center shadow-md">
                        <Star className="h-5 w-5 text-amber-900 mb-1" />
                        <span className="font-semibold">{topThree[2].userName}</span>
                        <span className="text-lg font-bold">{topThree[2].score}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Rest of leaderboard */}
            {!notStartedYet && restOfLeaderboard.length > 0 && (
              <div className="mt-8">
                <h4 className="text-sm font-medium mb-3">Weitere Teilnehmer</h4>
                <div className="bg-muted/20 rounded-md overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-2 text-left font-medium">Platz</th>
                        <th className="px-4 py-2 text-left font-medium">Name</th>
                        <th className="px-4 py-2 text-right font-medium">{getMetricLabel(challenge.metricType)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {restOfLeaderboard.map((entry) => (
                        <tr key={entry.userId} className="border-b border-muted/30 hover:bg-muted/30">
                          <td className="px-4 py-3">{entry.rank}</td>
                          <td className="px-4 py-3">{entry.userName}</td>
                          <td className="px-4 py-3 text-right font-medium">{entry.score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* No participants yet */}
            {!notStartedYet && entries.length === 0 && (
              <div className="text-center p-12">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium">Noch keine Teilnehmer</h3>
                <p className="text-muted-foreground mt-2">Sobald Mitarbeiter Leads abschließen, werden sie hier erscheinen.</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
